import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Event {
  id: string
  title: string
  event_type: string
  organizer: string
  date: string
  start_time: string
  end_time: string
  location: string | null
  is_virtual: boolean
  status: string
  attendees_count: number
  accepted_count: number
}

interface Reminder {
  id: string
  title: string
  reminder_type: string
  due_date: string
  due_time: string
  owner: string
  is_completed: boolean
  priority: string
}

export default function CalendarPage() {
  const [events, setEvents] = useState<Event[]>([])
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [evtRes, remRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/calendar/events'),
      fetch(API_BASE + '/calendar/reminders'),
      fetch(API_BASE + '/calendar/statistics')
    ])
    if (evtRes.ok) setEvents((await evtRes.json()).events || [])
    if (remRes.ok) setReminders((await remRes.json()).reminders || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800'
      case 'tentative': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'completed': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-gray-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Calendar</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Event</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Reminder</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Events Today</div>
              <div className="text-2xl font-bold">{statistics.events_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Next 7 Days</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.upcoming_events_7_days}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Virtual Meetings</div>
              <div className="text-2xl font-bold text-green-600">{statistics.virtual_meetings}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Reminders</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_reminders}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Attendance Rate</div>
              <div className="text-2xl font-bold">{statistics.avg_attendance_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Upcoming Events</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendees</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {events.slice(0, 12).map(evt => (
                  <tr key={evt.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {evt.is_virtual && <span className="text-blue-500">📹</span>}
                        <div>
                          <div className="font-medium">{evt.title}</div>
                          <div className="text-xs text-gray-500">{evt.organizer} • {evt.event_type.replace(/_/g, ' ')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{evt.date}</div>
                      <div className="text-xs text-gray-500">{evt.start_time} - {evt.end_time}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{evt.accepted_count}/{evt.attendees_count}</div>
                      <div className="text-xs text-gray-500">{evt.location || 'Virtual'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(evt.status)}`}>{evt.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Reminders</h3>
            <div className="space-y-3">
              {reminders.filter(r => !r.is_completed).slice(0, 10).map(rem => (
                <div key={rem.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{rem.title}</span>
                    <span className={`text-xs ${getPriorityColor(rem.priority)}`}>{rem.priority}</span>
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{rem.reminder_type.replace(/_/g, ' ')}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{rem.owner}</span>
                    <span className="text-xs text-gray-500">{rem.due_date} {rem.due_time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
